/**
 * MFA Setup Component (DC-904)
 *
 * Handles the MFA enrollment flow:
 * 1. Generate setup (QR code + backup codes)
 * 2. Display QR code for authenticator app
 * 3. Verify first TOTP code
 * 4. Display backup codes for safekeeping
 */

import React, { useState, useEffect, useRef } from 'react';
import { useAuthenticatedFetch } from '../../hooks/useAuth';
import { BackupCodesDisplay } from './BackupCodesDisplay';

interface MFASetupProps {
  onComplete: () => void;
  onCancel: () => void;
}

interface MFASetupData {
  secret: string;
  qrCodeUrl: string;
  backupCodes: string[];
}

type SetupStep = 'loading' | 'scan' | 'verify' | 'backup' | 'complete';

export function MFASetup({ onComplete, onCancel }: MFASetupProps) {
  const { authFetch } = useAuthenticatedFetch();
  const [step, setStep] = useState<SetupStep>('loading');
  const [setupData, setSetupData] = useState<MFASetupData | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Fetch MFA setup on mount
  useEffect(() => {
    const fetchSetup = async () => {
      try {
        const response = await authFetch('/api/iam/mfa/setup', {
          method: 'POST',
        });

        const data = await response.json();

        if (!response.ok) {
          setError(data.error?.message || 'Failed to generate MFA setup');
          setStep('scan'); // Show error state
          return;
        }

        setSetupData(data.data.mfa);
        setStep('scan');
      } catch {
        setError('Network error. Please try again.');
        setStep('scan');
      }
    };

    fetchSetup();
  }, [authFetch]);

  // Handle 6-digit code input
  const handleCodeChange = (index: number, value: string) => {
    // Only allow digits
    const digit = value.replace(/\D/g, '').slice(-1);

    const newCode = verificationCode.split('');
    newCode[index] = digit;
    const updatedCode = newCode.join('').slice(0, 6);
    setVerificationCode(updatedCode);

    // Auto-focus next input
    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when 6 digits entered
    if (updatedCode.length === 6) {
      handleVerify(updatedCode);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    setVerificationCode(pastedData);

    // Focus appropriate input
    if (pastedData.length === 6) {
      handleVerify(pastedData);
    } else {
      inputRefs.current[pastedData.length]?.focus();
    }
  };

  const handleVerify = async (code: string = verificationCode) => {
    if (code.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      const response = await authFetch('/api/iam/mfa/enable', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: code }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error?.message || 'Invalid verification code');
        setVerificationCode('');
        inputRefs.current[0]?.focus();
      } else {
        setStep('backup');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackupCodesSaved = () => {
    setStep('complete');
    onComplete();
  };

  // Loading state
  if (step === 'loading') {
    return (
      <div className="mfa-setup">
        <div className="mfa-setup-loading">
          <div className="mfa-spinner" />
          <p>Generating MFA setup...</p>
        </div>
      </div>
    );
  }

  // Scan QR code step
  if (step === 'scan') {
    return (
      <div className="mfa-setup">
        <h3 className="mfa-setup-title">Set Up Two-Factor Authentication</h3>
        <p className="mfa-setup-subtitle">
          Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
        </p>

        {error && (
          <div className="auth-error" style={{ marginBottom: '1rem' }}>
            <span className="auth-error-icon">!</span>
            {error}
          </div>
        )}

        {setupData && (
          <>
            <div className="mfa-qr-container">
              {/* QR Code rendered as an image using the otpauth:// URL */}
              <div className="mfa-qr-code">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(setupData.qrCodeUrl)}`}
                  alt="Scan this QR code with your authenticator app"
                  width={200}
                  height={200}
                />
              </div>
            </div>

            <div className="mfa-manual-entry">
              <button
                type="button"
                className="mfa-toggle-manual"
                onClick={() => {
                  const el = document.getElementById('mfa-secret-display');
                  if (el) el.style.display = el.style.display === 'none' ? 'block' : 'none';
                }}
              >
                Can't scan? Enter code manually
              </button>
              <div id="mfa-secret-display" className="mfa-secret-display" style={{ display: 'none' }}>
                <label>Secret Key:</label>
                <code className="mfa-secret-code">{setupData.secret}</code>
                <button
                  type="button"
                  className="mfa-copy-button"
                  onClick={() => navigator.clipboard.writeText(setupData.secret)}
                >
                  Copy
                </button>
              </div>
            </div>

            <div className="mfa-setup-actions">
              <button
                type="button"
                className="auth-button auth-button-secondary"
                onClick={onCancel}
              >
                Cancel
              </button>
              <button
                type="button"
                className="auth-button"
                onClick={() => setStep('verify')}
              >
                Next: Verify Code
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  // Verify code step
  if (step === 'verify') {
    return (
      <div className="mfa-setup">
        <h3 className="mfa-setup-title">Verify Your Authenticator</h3>
        <p className="mfa-setup-subtitle">
          Enter the 6-digit code from your authenticator app to verify setup
        </p>

        {error && (
          <div className="auth-error" style={{ marginBottom: '1rem' }}>
            <span className="auth-error-icon">!</span>
            {error}
          </div>
        )}

        <div className="mfa-code-input-container">
          <div className="mfa-code-inputs" onPaste={handlePaste}>
            {[0, 1, 2, 3, 4, 5].map((index) => (
              <input
                key={index}
                ref={(el) => { inputRefs.current[index] = el; }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                className="mfa-code-input"
                value={verificationCode[index] || ''}
                onChange={(e) => handleCodeChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                disabled={isSubmitting}
                autoFocus={index === 0}
              />
            ))}
          </div>
        </div>

        <div className="mfa-setup-actions">
          <button
            type="button"
            className="auth-button auth-button-secondary"
            onClick={() => {
              setStep('scan');
              setVerificationCode('');
              setError(null);
            }}
            disabled={isSubmitting}
          >
            Back
          </button>
          <button
            type="button"
            className="auth-button"
            onClick={() => handleVerify()}
            disabled={isSubmitting || verificationCode.length !== 6}
          >
            {isSubmitting ? (
              <>
                <span className="auth-button-spinner" />
                Verifying...
              </>
            ) : (
              'Verify & Enable'
            )}
          </button>
        </div>
      </div>
    );
  }

  // Show backup codes step
  if (step === 'backup' && setupData) {
    return (
      <BackupCodesDisplay
        backupCodes={setupData.backupCodes}
        onContinue={handleBackupCodesSaved}
        isInitialSetup={true}
      />
    );
  }

  return null;
}

export default MFASetup;
